// Live usage summary for the top-bar rings + plan context (PRD §4.2, §8.1).
// Replaces the hardcoded USAGE_RINGS mock: subscribers = live count, messages =
// sum of real broadcast sends, AI tokens = 0 until the AI agent lands (P6).
// Limits come from the tenant's plan (mirror of functions/src/tenancy/plans.ts).
import { useEffect, useState } from 'react';
import {
  collection, doc, getCountFromServer, getDocs, limit as qlimit, onSnapshot, query,
} from 'firebase/firestore';
import { db } from './firebase';
import { useTenant } from './tenant';
import type { UsageRingData } from '../mocks/dashboard';
import { PLAN_CATALOG, planFromDoc, applyOverrides } from './billing';

export interface Quotas {
  subscribers: number;
  messagesPerMonth: number;
  aiTokensPerMonth: number;
  teamSeats: number;
}

/**
 * Static fallback only — derived from PLAN_CATALOG so a new plan (e.g.
 * `business`) can never silently fall back to Free limits again. Live limits
 * come from `useQuotas()` (Firestore `plans/{id}` + tenant `overrides`), which
 * is what Platform Admin edits and what the server-side quota guard enforces.
 */
export const PLAN_QUOTAS: Record<string, Quotas> = Object.fromEntries(
  Object.entries(PLAN_CATALOG).map(([id, p]) => [id, {
    subscribers: p.quotas.subscribers,
    messagesPerMonth: p.quotas.messagesPerMonth,
    aiTokensPerMonth: p.quotas.aiTokensPerMonth,
    teamSeats: p.quotas.teamSeats,
  }]),
);

/** Resolve a plan's quotas from the static catalog (falls back to Free). */
export function quotasFor(planId: string | null): Quotas {
  return (planId && PLAN_QUOTAS[planId]) || FALLBACK;
}

/**
 * Live quotas for the current tenant: its planId → Firestore `plans/{planId}`
 * merged over the static catalog, then per-tenant `overrides` on top.
 */
export function useQuotas(): { planId: string | null; quotas: Quotas } {
  const { tenantId } = useTenant();
  const [planId, setPlanId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<unknown>(null);
  const [planDoc, setPlanDoc] = useState<unknown>(null);

  useEffect(() => {
    if (!tenantId) { setPlanId(null); setOverrides(null); return; }
    return onSnapshot(
      doc(db, `tenants/${tenantId}`),
      (s) => {
        const d = s.exists() ? s.data() : null;
        setPlanId((d?.planId as string) ?? null);
        setOverrides(d?.overrides ?? null);
      },
      () => { setPlanId(null); setOverrides(null); },
    );
  }, [tenantId]);

  useEffect(() => {
    if (!planId) { setPlanDoc(null); return; }
    return onSnapshot(
      doc(db, `plans/${planId}`),
      (s) => setPlanDoc(s.exists() ? s.data() : null),
      () => setPlanDoc(null),
    );
  }, [planId]);

  const base = (planId && planFromDoc(planId, planDoc, PLAN_CATALOG[planId])) || PLAN_CATALOG.trial;
  const q = applyOverrides(base, overrides).quotas;
  return {
    planId,
    quotas: {
      subscribers: q.subscribers,
      messagesPerMonth: q.messagesPerMonth,
      aiTokensPerMonth: q.aiTokensPerMonth,
      teamSeats: q.teamSeats,
    },
  };
}

/** Lightweight hook: the tenant's current planId (for seat/quota lookups). */
export function usePlanId(): string | null {
  const { tenantId } = useTenant();
  const [planId, setPlanId] = useState<string | null>(null);
  useEffect(() => {
    if (!tenantId) { setPlanId(null); return; }
    return onSnapshot(
      doc(db, `tenants/${tenantId}`),
      (s) => setPlanId(s.exists() ? ((s.data().planId as string) ?? null) : null),
      () => setPlanId(null),
    );
  }, [tenantId]);
  return planId;
}
const FALLBACK: Quotas = PLAN_QUOTAS.trial;

export interface UsageSummary {
  rings: UsageRingData[];
  planId: string | null;
  /** Live subscriber count (also exposed for the dashboard cards). */
  subscribers: number;
  loading: boolean;
}

/** Three live usage rings for the global topbar (subscribers / messages / AI). */
export function useUsageSummary(): UsageSummary {
  const { tenantId } = useTenant();
  // Live plan + limits (plans/{id} + tenant overrides) — never a stale mirror.
  const { planId, quotas: q } = useQuotas();
  const [subscribers, setSubscribers] = useState(0);
  const [messages, setMessages] = useState(0);
  const [loading, setLoading] = useState(true);

  // Subscriber count + total messages sent (cheap count + a bounded doc read).
  useEffect(() => {
    let alive = true;
    if (!tenantId) { setSubscribers(0); setMessages(0); setLoading(false); return; }
    setLoading(true);
    void (async () => {
      try {
        const c = await getCountFromServer(collection(db, `tenants/${tenantId}/subscribers`));
        if (alive) setSubscribers(c.data().count);
      } catch { /* count unavailable — leave 0 */ }
      try {
        const snap = await getDocs(
          query(collection(db, `tenants/${tenantId}/broadcasts`), qlimit(200)),
        );
        let sent = 0;
        snap.forEach((d) => { sent += ((d.data().counters?.sent as number) ?? 0); });
        if (alive) setMessages(sent);
      } catch { /* ignore */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [tenantId]);

  const rings: UsageRingData[] = [
    { key: 'topbar.subscribers', used: subscribers, limit: q.subscribers },
    { key: 'topbar.messages', used: messages, limit: q.messagesPerMonth },
    { key: 'topbar.aiTokens', used: 0, limit: q.aiTokensPerMonth },
  ];
  return { rings, planId, subscribers, loading };
}
